"use client";

import TokenService from "./tokenService";

// Authentication service for API calls
const BASE_URL = "https://api.thedhms.com/family";


class AuthService {
  // User registration - POST /family/usersignup
  // Body: { fullname, email, phone (nullable), password }
  static async userSignup(userData) {
    try {
      const payload = {
        fullname: userData.fullName,
        email: userData.email,
        phone: userData.phone?.trim() || null,
        password: userData.password,
      };
      const response = await fetch(`${BASE_URL}/usersignup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error(
          "Server returned non-JSON response:",
          text.substring(0, 200)
        );
        throw new Error(
          "Server error: Unable to process request. Please try again later."
        );
      }
      const data = await response.json();

      // Check HTTP-level errors
      if (!response.ok) {
        throw new Error(data.message || `Server error: ${response.status}`);
      }

      // Check body-level errors: API may return HTTP 200 but signal failure in body
      if (data.status && data.status >= 400) {
        throw new Error(data.message || `Error: ${data.status}`);
      }
      if (data.errors) {
        const firstError = Object.values(data.errors)[0];
        throw new Error(firstError || data.message || "Signup failed. Please try again.");
      }

      const tokens = {};

      if (data.Token && typeof data.Token === "object") {
        if (data.Token.access) tokens.accessToken = data.Token.access;
        if (data.Token.refresh) tokens.refreshToken = data.Token.refresh;
      }
      if (data.token) tokens.accessToken = data.token;
      if (data.access) tokens.accessToken = data.access;
      if (data.accessToken) tokens.accessToken = data.accessToken;
      if (data.refresh) tokens.refreshToken = data.refresh;
      if (data.refreshToken) tokens.refreshToken = data.refreshToken;

      if (Object.keys(tokens).length > 0) {
        this.storeTokens(tokens);
      }

      // Store user data — prefer what the API returns, fall back to what the user typed
      const userObj = data.user || data.User;
      const storedName = userObj
        ? (userObj.fullname || userObj.fullName || userObj.name || userObj.username)
        : (data.fullname || data.fullName || data.name || userData.fullName);
      localStorage.setItem("userData", JSON.stringify({
        name: storedName,
        email: userData.email,
        username: userObj ? userObj.username : data.username,
        avatar: userObj
          ? (userObj.avatar || userObj.profileImage || userObj.image)
          : (data.avatar || data.profileImage || null),
      }));

      return { ...data, tokens };
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error("Network error: Please check your internet connection.");
      }
      throw error;
    }
  }

  // Google signup/login
  static async googleSignup(userInfo) {
    try {
      const payload = {
        email: userInfo.email,
        fullName: userInfo.fullName,
        googleId: userInfo.googleId,
        profileImage: userInfo.profileImage || "",
      };
      const response = await fetch(`${BASE_URL}/googlesignup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error(
          "Server returned non-JSON response:",
          text.substring(0, 200)
        );
        throw new Error(
          "Server error: Unable to process Google signup. Please try again later."
        );
      }
      const data = await response.json();
      console.log("[AuthService] Google signup response data:", data);

      if (!response.ok) {
        throw new Error(
          data.message || `Google signup failed: ${response.status}`
        );
      }

      // Extract and store tokens from response if they exist
      const tokens = {};

      // Backend may return tokens in nested "Token" object (with capital T)
      if (data.Token && typeof data.Token === "object") {
        console.log(
          "[AuthService] Found nested Token object in Google signup:",
          Object.keys(data.Token)
        );
        if (data.Token.access) tokens.accessToken = data.Token.access;
        if (data.Token.refresh) tokens.refreshToken = data.Token.refresh;
      }

      // Also check flat structure
      if (data.token) tokens.accessToken = data.token;
      if (data.access) tokens.accessToken = data.access;
      if (data.accessToken) tokens.accessToken = data.accessToken;
      if (data.refresh) tokens.refreshToken = data.refresh;
      if (data.refreshToken) tokens.refreshToken = data.refreshToken;

      // Store tokens immediately if provided
      if (Object.keys(tokens).length > 0) {
        console.log(
          "[AuthService] Storing tokens from Google signin:",
          Object.keys(tokens)
        );
        this.storeTokens(tokens);
      }

      // Store user data from Google signup
      if (data.user || data.User || data.fullname || data.fullName) {
        const userObj = data.user || data.User;
        const userData = {
          name: userObj
            ? userObj.fullname ||
              userObj.fullName ||
              userObj.name ||
              userObj.username
            : data.fullname || data.fullName || data.name || data.username,
          email: userObj ? userObj.email : data.email,
          username: userObj ? userObj.username : data.username,
          avatar: userObj
            ? userObj.avatar || userObj.profileImage || userObj.image
            : data.avatar || data.profileImage || data.image,
        };
        console.log(
          "[AuthService] Storing user data from Google signup:",
          userData
        );
        localStorage.setItem("userData", JSON.stringify(userData));
      } else {
        const userData = {
          name: userInfo.fullName || userInfo.name,
          email: userInfo.email,
          avatar: userInfo.profileImage,
        };
        console.log(
          "[AuthService] Storing user data from Google signup:",
          userData
        );
        localStorage.setItem("userData", JSON.stringify(userData));
      }

      return { ...data, tokens };
    } catch (error) {
      console.error("Google signup error:", error);
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error: Please check your internet connection."
        );
      }
      throw error;
    }
  }

  // Google Sign-In - POST /family/googlesignin
  // Body: { email: string (minLength: 1), name: string (minLength: 1) }
  static async googleSignin(credentials) {
    try {
      const payload = {
        email: credentials.email,
        name: credentials.name,
      };
      const response = await fetch(`${BASE_URL}/googlesignin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server error: Unable to process Google signin. Please try again later.");
      }
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Google signin failed: ${response.status}`);
      }

      const tokens = {};
      if (data.Token && typeof data.Token === "object") {
        if (data.Token.access) tokens.accessToken = data.Token.access;
        if (data.Token.refresh) tokens.refreshToken = data.Token.refresh;
      }
      if (data.token) tokens.accessToken = data.token;
      if (data.access) tokens.accessToken = data.access;
      if (data.accessToken) tokens.accessToken = data.accessToken;
      if (data.refresh) tokens.refreshToken = data.refresh;
      if (data.refreshToken) tokens.refreshToken = data.refreshToken;

      if (Object.keys(tokens).length > 0) {
        this.storeTokens(tokens);
      }

      // Store user data — prefer what the backend returns, fall back to Google credentials
      const userObj = data.user || data.User;
      const userData = {
        name: userObj
          ? userObj.fullname || userObj.fullName || userObj.name || userObj.username
          : data.fullname || data.fullName || data.name || credentials.name,
        email: userObj ? userObj.email : (data.email || credentials.email),
        username: userObj ? userObj.username : data.username,
        avatar: userObj
          ? userObj.avatar || userObj.profileImage || userObj.image
          : data.avatar || data.profileImage || credentials.picture || null,
      };
      localStorage.setItem("userData", JSON.stringify(userData));

      return { ...data, tokens };
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error("Network error: Please check your internet connection.");
      }
      throw error;
    }
  }

  // User login - POST /family/userlogin
  // Body: { username: string, password: string }
  static async userLogin(loginData) {
    try {
      const payload = {
        username: loginData.email.trim().toLowerCase(),
        password: loginData.password,
      };
      const response = await fetch(`${BASE_URL}/userlogin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error(
          "Server returned non-JSON response:",
          text.substring(0, 200)
        );
        throw new Error(
          "Server error: Unable to process login. Please try again later."
        );
      }
      const data = await response.json();
      console.log("[AuthService] Login response data:", data);

      if (!response.ok) {
        throw new Error(data.message || `Login failed: ${response.status}`);
      }

      // Explicit failure flags in response body
      if (data.error || data.success === false) {
        throw new Error(
          data.message || data.error || "Login failed. Please check your credentials."
        );
      }

      // Extract and store tokens from response if they exist
      const tokens = {};

      // Backend may return tokens in nested "Token" object (with capital T)
      if (data.Token && typeof data.Token === "object") {
        console.log(
          "[AuthService] Found nested Token object in login:",
          Object.keys(data.Token)
        );
        if (data.Token.access) tokens.accessToken = data.Token.access;
        if (data.Token.refresh) tokens.refreshToken = data.Token.refresh;
      }

      // Also check flat structure
      if (data.token) tokens.accessToken = data.token;
      if (data.access) tokens.accessToken = data.access;
      if (data.accessToken) tokens.accessToken = data.accessToken;
      if (data.refresh) tokens.refreshToken = data.refresh;
      if (data.refreshToken) tokens.refreshToken = data.refreshToken;

      // Store tokens immediately if provided
      if (Object.keys(tokens).length > 0) {
        console.log(
          "[AuthService] Storing tokens from login:",
          Object.keys(tokens)
        );
        this.storeTokens(tokens);
      }

      // Store user data if available
      console.log(
        "[AuthService] Full API response data:",
        JSON.stringify(data, null, 2)
      );
      console.log("[AuthService] Checking user data - data.user:", data.user);
      console.log("[AuthService] Checking user data - data.User:", data.User);

      let userData = null;

      if (data.user || data.User) {
        const userObj = data.user || data.User;
        console.log("[AuthService] Found nested user object:", userObj);
        userData = {
          name:
            userObj.fullname ||
            userObj.fullName ||
            userObj.name ||
            userObj.username,
          email: userObj.email,
          username: userObj.username,
          avatar: userObj.avatar || userObj.profileImage || userObj.image,
        };
        console.log(
          "[AuthService] Storing user data from nested object:",
          userData
        );
        localStorage.setItem("userData", JSON.stringify(userData));
      } else if (
        data.fullname ||
        data.fullName ||
        data.name ||
        data.email ||
        data.username
      ) {
        // Store flat user data
        console.log("[AuthService] Using flat data structure");
        userData = {
          name: data.fullname || data.fullName || data.name || data.username,
          email: data.email,
          username: data.username,
          avatar: data.avatar || data.profileImage || data.image,
        };
        console.log("[AuthService] Storing user data (flat):", userData);
        localStorage.setItem("userData", JSON.stringify(userData));
      } else {
        console.warn("[AuthService] No user data found in response — will fetch from profile endpoint");
      }

      // Always fetch from profile if we have no name yet
      if (!userData || !userData.name) {
        try {
          const profileResponse = await fetch(
            "https://api.thedhms.com/family/userprofile",
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${tokens.accessToken}`,
              },
            }
          );

          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            const profileUser = profileData.data || profileData;
            const fullName =
              profileUser.fullname ||
              profileUser.fullName ||
              profileUser.name ||
              profileUser.username ||
              profileUser.email?.split("@")[0] ||
              null;

            const email = profileUser.email || (userData && userData.email) || loginData.email;
            userData = {
              name: fullName,
              email,
              username: profileUser.username || (userData && userData.username),
              avatar: profileUser.avatar || profileUser.profileImage || profileUser.image || null,
            };
            localStorage.setItem("userData", JSON.stringify(userData));
            window.dispatchEvent(new CustomEvent("tokensChanged"));
          }
        } catch (error) {
          console.error("[AuthService] Failed to fetch profile:", error);
          if (!userData) {
            userData = { name: loginData.email?.split("@")[0] || "User", email: loginData.email };
            localStorage.setItem("userData", JSON.stringify(userData));
          }
        }
      }

      return { ...data, tokens };
    } catch (error) {
      console.error("Login error:", error);
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error: Please check your internet connection."
        );
      }
      if (error.message.includes("Unexpected token")) {
        throw new Error(
          "Server error: Invalid response format. Please try again later."
        );
      }
      throw error;
    }
  }

  // Reset password using email address + sends notification - PUT /setnewpasswordwithnotification
  static async resetPasswordWithNotification({ emailaddress, newPassword }) {
    try {
      const response = await fetch(
        `${BASE_URL}/setnewpasswordwithnotification`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ emailaddress, newPassword }),
        }
      );
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        if (response.ok && (text.includes("success") || text.includes("updated"))) {
          return { success: true, message: "Password updated successfully" };
        }
        throw new Error(
          "Server error: Unable to reset password. Please try again later."
        );
      }
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.message || `Password reset failed: ${response.status}`
        );
      }
      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error("Network error: Please check your internet connection.");
      }
      if (error.message.includes("400")) {
        throw new Error("Invalid email address format.");
      }
      if (error.message.includes("404")) {
        throw new Error("No account found with this email address.");
      }
      if (error.message.includes("429")) {
        throw new Error(
          "Too many requests. Please wait a few minutes before trying again."
        );
      }
      if (error.message.includes("500")) {
        throw new Error("Server error. Please try again later.");
      }
      throw error;
    }
  }

  // Reset password with ID/token (from email link) - PUT /setnewpasswordwithid
  static async resetPasswordWithId({ id, newPassword }) {
    try {
      const response = await fetch(`${BASE_URL}/setnewpasswordwithid`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ id, newPassword }),
      });
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        if (response.ok && (text.includes("success") || text.includes("updated"))) {
          return { success: true, message: "Password updated successfully" };
        }
        throw new Error("Server error: Unable to reset password. Please try again later.");
      }
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `Password reset failed: ${response.status}`);
      }
      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error("Network error: Please check your internet connection.");
      }
      if (error.message.includes("400")) {
        throw new Error("Invalid reset data. Please check your information.");
      }
      if (error.message.includes("401")) {
        throw new Error("Reset token has expired. Please request a new password reset.");
      }
      if (error.message.includes("404")) {
        throw new Error("Invalid reset token. Please request a new password reset.");
      }
      if (error.message.includes("422")) {
        throw new Error("Password does not meet security requirements.");
      }
      throw error;
    }
  }

  // Update password (requires old password) - PUT /updatepassword
  static async updatePassword(passwordData) {
    try {
      // POST /family/profile/change-password/
      const payload = {
        currentPassword: passwordData.currentPassword || passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      };
      const response = await fetch(`${BASE_URL}/profile/change-password/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${this.getAccessToken()}`,
        },
        body: JSON.stringify(payload),
      });
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error(
          "Server returned non-JSON response:",
          text.substring(0, 200)
        );
        if (
          response.ok &&
          (text.includes("success") || text.includes("updated"))
        ) {
          return {
            success: true,
            message: "Password updated successfully",
          };
        }
        throw new Error(
          "Server error: Unable to update password. Please try again later."
        );
      }
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.message || `Password update failed: ${response.status}`
        );
      }
      return data;
    } catch (error) {
      console.error("Password update error:", error);
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error: Please check your internet connection."
        );
      }
      if (error.message.includes("401")) {
        throw new Error("Current password is incorrect.");
      }
      throw error;
    }
  }

  // Validate user with email code - POST /family/validateuserwithcode
  // Body: { code: string, email: string }
  static async validateUserWithCode(validationData) {
    try {
      const payload = {
        code: validationData.code,
        email: validationData.email,
      };

      const response = await fetch(`${BASE_URL}/validateuserwithcode`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server error: Unable to validate code. Please try again later.");
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || "Invalid verification code. Please try again.");
      }

      // Body-level error: API may return HTTP 200 with error status in body
      if (data.status && data.status >= 400) {
        throw new Error(data.message || "Verification failed. Please try again.");
      }
      if (data.errors) {
        const firstError = Object.values(data.errors)[0];
        throw new Error(firstError || data.message || "Verification failed. Please try again.");
      }
      if (data.error || data.success === false) {
        throw new Error(data.message || data.error || "Invalid verification code");
      }

      const tokens = {};

      if (data.Token && typeof data.Token === "object") {
        if (data.Token.access) tokens.accessToken = data.Token.access;
        if (data.Token.refresh) tokens.refreshToken = data.Token.refresh;
      }
      if (data.token) tokens.accessToken = data.token;
      if (data.access) tokens.accessToken = data.access;
      if (data.accessToken) tokens.accessToken = data.accessToken;
      if (data.refresh) tokens.refreshToken = data.refresh;
      if (data.refreshToken) tokens.refreshToken = data.refreshToken;

      if (Object.keys(tokens).length > 0) {
        this.storeTokens(tokens);
      }

      // Store user data — response nests user info under data.data
      const existingRaw = localStorage.getItem("userData");
      const existing = existingRaw ? JSON.parse(existingRaw) : {};
      const userObj = data.data || data.user || data.User;
      const resolvedName = userObj
        ? (userObj.name || userObj.fullname || userObj.fullName || userObj.username)
        : (data.fullname || data.fullName || data.name || existing.name);
      localStorage.setItem("userData", JSON.stringify({
        ...existing,
        name: resolvedName,
        email: userObj ? userObj.email : (data.email || validationData.email || existing.email),
        username: userObj ? (userObj.username || userObj.UniqueID) : (data.username || existing.username),
        avatar: userObj
          ? (userObj.avatar || userObj.profileImage || userObj.image || null)
          : (data.avatar || data.profileImage || existing.avatar || null),
      }));
      window.dispatchEvent(new CustomEvent("tokensChanged"));

      return { ...data, tokens };
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error("Network error: Please check your internet connection.");
      }
      throw error;
    }
  }

  // Request verification code to be sent
  static async requestVerificationCode(email) {
    try {
      // The backend should automatically send code on signup/login
      // This is a fallback/resend method if needed
      const response = await fetch(`${BASE_URL}/resendverificationcode`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ email }),
      });
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error(
          "Server returned non-JSON response:",
          text.substring(0, 200)
        );
        throw new Error(
          "Server error: Unable to send verification code. Please try again later."
        );
      }
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to send verification code");
      }
      return data;
    } catch (error) {
      console.error("Verification code request error:", error);
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          "Network error: Please check your internet connection."
        );
      }
      throw error;
    }
  }

  // Validate user with unique ID - POST /family/validateuserwithid
  // Body: { userdhmsid: string, email: string }
  static async validateUserWithId(validationData) {
    try {
      const payload = {
        userdhmsid: validationData.userdhmsid,
        email: validationData.email,
      };

      const response = await fetch(`${BASE_URL}/validateuserwithid`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server error: Unable to validate ID. Please try again later.");
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || "Invalid unique ID. Please try again.");
      }

      // Body-level error: API may return HTTP 200 with error status in body
      if (data.status && data.status >= 400) {
        throw new Error(data.message || "Verification failed. Please try again.");
      }
      if (data.errors) {
        const firstError = Object.values(data.errors)[0];
        throw new Error(firstError || data.message || "Verification failed. Please try again.");
      }
      if (data.error || data.success === false) {
        throw new Error(data.message || data.error || "Invalid unique ID");
      }

      const tokens = {};

      if (data.Token && typeof data.Token === "object") {
        if (data.Token.access) tokens.accessToken = data.Token.access;
        if (data.Token.refresh) tokens.refreshToken = data.Token.refresh;
      }
      if (data.token) tokens.accessToken = data.token;
      if (data.access) tokens.accessToken = data.access;
      if (data.accessToken) tokens.accessToken = data.accessToken;
      if (data.refresh) tokens.refreshToken = data.refresh;
      if (data.refreshToken) tokens.refreshToken = data.refreshToken;

      if (Object.keys(tokens).length > 0) {
        this.storeTokens(tokens);
      }

      return { ...data, tokens };
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error("Network error: Please check your internet connection.");
      }
      throw error;
    }
  }

  // Store authentication tokens
  static storeTokens(tokens) {
    TokenService.storeTokens(tokens);
  }

  // Get stored token
  static getAccessToken() {
    return TokenService.getAccessToken();
  }

  static getRefreshToken() {
    return TokenService.getRefreshToken();
  }

  // Clear stored tokens
  static clearTokens() {
    TokenService.clearTokens();
  }

  // Get stored user data
  static getUserData() {
    try {
      const userData = localStorage.getItem("userData");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("[AuthService] Error parsing user data:", error);
      return null;
    }
  }

  // Clear stored user data
  static clearUserData() {
    try {
      localStorage.removeItem("userData");
      console.log("[AuthService] User data cleared");
    } catch (error) {
      console.error("[AuthService] Error clearing user data:", error);
    }
  }

  // Check if user is authenticated
  static isAuthenticated() {
    return !!this.getAccessToken();
  }

  // Test API connectivity
  static async testConnection() {
    try {
      const response = await fetch(`${BASE_URL}/health`, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });
      return response.ok;
    } catch (error) {
      console.error("Connection test failed:", error);
      return false;
    }
  }
}

// Export the AuthService class as default
export default AuthService;
